import { Text } from '@amelio/ui-library';
import { useEffect, useRef } from 'react';

import type { ReviewQuestion, ReviewQuestionOption } from '@/types/review/ReviewQuestion';

import { RatingScaleFormat } from '@/components/RatingScaleFormat/RatingScaleFormat';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

interface RatingQuestionProps {
    question: ReviewQuestion;
    setQuestion: (question: ReviewQuestion) => void;
    // Competency-page-only rating-scale bound labels; undefined for other modules.
    scaleFromLabel?: string;
    scaleToLabel?: string;
}

const DEFAULT_FROM_VALUE = 1;
const DEFAULT_TO_VALUE = 7;

export const RatingQuestion = ({
    question,
    setQuestion,
    scaleFromLabel,
    scaleToLabel,
}: RatingQuestionProps) => {
    const { t } = useAmelioTranslations();
    const defaultLanguage = useSelectedAppLanguage();
    const isMounted = useRef(false);

    const options = question.reviewQuestionOptions ?? [];
    const sortedOptions = [...options].sort((a, b) => a.sortOrder - b.sortOrder);
    const lowerBoundOption = sortedOptions[0];
    const upperBoundOption = sortedOptions[1];

    const fromValue = lowerBoundOption?.scoreValue?.toString() ?? '';
    const toValue = upperBoundOption?.scoreValue?.toString() ?? '';
    const fromText = getTextByLanguage(lowerBoundOption?.label, defaultLanguage) ?? '';
    const toText = getTextByLanguage(upperBoundOption?.label, defaultLanguage) ?? '';

    const updateQuestion = (newData: {
        fromValue: string;
        toValue: string;
        fromText?: string;
        toText?: string;
    }) => {
        /* Preserve previous scoreValue when the input was cleared, so an empty UI state
         * never forces an out-of-range value (e.g. 0) into the persisted question. */
        const fromScoreValue =
            newData.fromValue === ''
                ? (lowerBoundOption?.scoreValue ?? DEFAULT_FROM_VALUE)
                : parseInt(newData.fromValue, 10);
        const toScoreValue =
            newData.toValue === ''
                ? (upperBoundOption?.scoreValue ?? DEFAULT_TO_VALUE)
                : parseInt(newData.toValue, 10);

        const updatedOptions: ReviewQuestionOption[] = [
            {
                id: lowerBoundOption?.id ?? crypto.randomUUID(),
                label: {
                    ...lowerBoundOption?.label,
                    [defaultLanguage]: newData.fromText ?? fromText,
                },
                sortOrder: 0,
                scoreValue: fromScoreValue,
                reviewQuestionId: lowerBoundOption?.reviewQuestionId,
            },
            {
                id: upperBoundOption?.id ?? crypto.randomUUID(),
                label: {
                    ...upperBoundOption?.label,
                    [defaultLanguage]: newData.toText ?? toText,
                },
                sortOrder: 1,
                scoreValue: toScoreValue,
                reviewQuestionId: upperBoundOption?.reviewQuestionId,
            },
        ];

        setQuestion({
            ...question,
            reviewQuestionOptions: updatedOptions,
        });
    };

    useEffect(() => {
        if (options.length !== 0) {
            return;
        }

        if (isMounted.current) {
            return;
        }

        isMounted.current = true;

        const defaultOptions: ReviewQuestionOption[] = [
            {
                id: crypto.randomUUID(),
                label: {
                    [defaultLanguage]:
                        scaleFromLabel ??
                        t(
                            'evaluation360Questionnaire.questionForm.questions.common.stronglyDisagree',
                        ),
                },
                sortOrder: 0,
                scoreValue: DEFAULT_FROM_VALUE,
            },
            {
                id: crypto.randomUUID(),
                label: {
                    [defaultLanguage]:
                        scaleToLabel ??
                        t('evaluation360Questionnaire.questionForm.questions.common.stronglyAgree'),
                },
                sortOrder: 1,
                scoreValue: DEFAULT_TO_VALUE,
            },
        ];
        setQuestion({
            ...question,
            reviewQuestionOptions: defaultOptions,
        });
    }, [options.length, question, setQuestion, defaultLanguage, t, scaleFromLabel, scaleToLabel]);

    return (
        <div>
            <Text
                className={'mt-8'}
                weight={'medium'}
            >
                {t('evaluation360Questionnaire.questionForm.questions.rating.longTitle')}
            </Text>

            <RatingScaleFormat
                fromValue={fromValue}
                toValue={toValue}
                fromText={fromText}
                toText={toText}
                onChange={updateQuestion}
            />
        </div>
    );
};
